import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import ReservationForm from "@/components/ReservationForm";
import { NoAutoCancelToggle } from "@/components/NoAutoCancelToggle";
import { ArrowLeft } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatRp } from "@/lib/constants";

function NewBillDialog({ bill, oldTotal, onDone }) {
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    setBusy(true);
    try { await api.post(`/reservations/${bill.id}/resend-payment-link`); toast.success("Link pembayaran dikirim ulang"); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!bill} onOpenChange={(o) => { if (!o) onDone(); }}>
      <DialogContent data-testid="new-bill-dialog" className="max-w-md">
        <DialogHeader><DialogTitle>Tagihan diperbarui</DialogTitle></DialogHeader>
        {bill && (
          <div className="space-y-2 text-sm text-slate-700">
            <p>Total: <s className="text-slate-400">{formatRp(oldTotal)}</s> → <b data-testid="new-bill-total">{formatRp(bill.total_tagihan)}</b></p>
            {bill.payment_mode === "dp" && <p>DP minimal: <b>{formatRp(bill.dp_required)}</b></p>}
            <p>Sisa tagihan: <b data-testid="new-bill-sisa">{formatRp(bill.sisa_tagihan)}</b></p>
            {bill.payment_expires_at && <p className="text-xs text-slate-500">Batas bayar: {new Date(bill.payment_expires_at).toLocaleString("id-ID", { timeZone: "Asia/Makassar" })} WITA</p>}
            <div className="grid grid-cols-2 gap-2 pt-2">
              <button data-testid="new-bill-done" onClick={onDone} className="rounded-xl bg-slate-100 py-2.5 font-bold text-slate-600 hover:bg-slate-200">Selesai</button>
              <button data-testid="new-bill-resend" disabled={busy} onClick={resend} className="rounded-xl bg-[#25D366] py-2.5 font-bold text-white hover:bg-[#20b858] disabled:opacity-60">{busy ? "Mengirim…" : "Kirim ulang link pembayaran"}</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function EditReservation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [initial, setInitial] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bill, setBill] = useState(null);

  useEffect(() => {
    // Orders are NOT single reservations — never fetch them from the reservation edit route.
    if (String(id).startsWith("order:") || String(id).startsWith("order")) {
      navigate("/pesanan", { replace: true });
      return;
    }
    api
      .get(`/reservations/${id}`)
      .then((r) => {
        const d = r.data;
        const clean = Object.fromEntries(
          Object.entries(d).map(([k, v]) => [k, (v === null && k !== "alamat_data") ? "" : v])
        );
        const ps = d.payment_status || "";
        clean.status_pembayaran = ps === "Lunas" ? "Lunas"
          : /refund/i.test(ps) ? "Refund"
          : /(dp|sebagian)/i.test(ps) ? "DP"
          : "Belum Bayar";
        setInitial(clean);
      })
      .catch((e) => { setLoadError(true); toast.error(apiError(e)); });
  }, [id, navigate]);

  const submit = async (payload) => {
    setSubmitting(true);
    try {
      const { data } = await api.put(`/reservations/${id}`, payload);
      toast.success("Reservasi diperbarui");
      const changed = Math.round(Number(data?.total_tagihan || 0)) !== Math.round(Number(initial.total_tagihan || 0));
      if (changed && ["dp", "full"].includes(data?.payment_mode) && Number(data?.sisa_tagihan || 0) > 0) setBill(data);
      else navigate("/reservasi");
    } catch (e) {
      console.error("Edit reservasi gagal:", e.response?.data || e);
      toast.error(e.response?.status === 422
        ? "Perubahan pembayaran belum dapat disimpan. Silakan periksa kembali dan coba lagi."
        : apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError)
    return (
      <div className="animate-fade-up mx-auto max-w-3xl py-16 text-center" data-testid="edit-load-error">
        <p className="text-sm font-semibold text-slate-600">Data reservasi tidak ditemukan atau tidak dapat dimuat.</p>
        <button data-testid="edit-error-back" onClick={() => navigate(-1)} className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-[#7C4DE0] px-4 py-2 text-sm font-bold text-white hover:bg-[#6A38C7]">
          <ArrowLeft size={16} /> Kembali
        </button>
      </div>
    );

  if (!initial)
    return (
      <div className="flex justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-rose-200 border-t-rose-500" />
      </div>
    );

  return (
    <div className="animate-fade-up mx-auto max-w-3xl space-y-6">
      <div>
        <button
          onClick={() => navigate(-1)}
          className="mb-3 flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-[#6A38C7]"
        >
          <ArrowLeft size={18} /> Kembali
        </button>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-800">Edit Reservasi</h1>
      </div>
      {initial.status !== "Dibatalkan Otomatis" && <NoAutoCancelToggle kind="reservation" id={id} initial={initial.no_auto_cancel} />}
      <ReservationForm initial={initial} onSubmit={submit} submitting={submitting} submitLabel="Simpan Perubahan" />
      <NewBillDialog bill={bill} oldTotal={initial.total_tagihan} onDone={() => navigate("/reservasi")} />
    </div>
  );
}
